import React, { useEffect, useMemo, useState } from 'react';
import apiClient from '../../utils/api';
import { Button } from '../ui/Button';

// Logging templates (SS0) — the one-tap, adjustable log bundle.
//
// A template is a saved set of would-be entries ("morning stack", "leg day").
// Logging it EXPANDS its items into editable rows: uncheck what you skipped,
// tweak a value, pick the date, and the survivors POST straight to the existing
// /api/health/entries (source="template"). No new write path — the same proven
// bulk endpoint the conversational logger uses. This is the friction-killer:
// give us your data in one tap instead of retyping the same stack every day.

const inputCls =
  'flex h-9 w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm ' +
  'placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:border-surface-dark-border ' +
  'dark:bg-surface-dark-raised/50 dark:text-white dark:ring-offset-gray-900 ' +
  'dark:placeholder:text-gray-400 dark:focus-visible:ring-primary-400';

const labelCls = 'text-sm font-medium text-gray-700 dark:text-gray-200';

const itemLabel = (it) =>
  (it.entry_type || 'item').replace(/_/g, ' ');

const TemplatePicker = ({ defaultDate, onEntrySaved }) => {
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  // Apply state
  const [applyingId, setApplyingId] = useState(null);
  const [rows, setRows] = useState([]);          // {…item, include, value}
  const [applyDate, setApplyDate] = useState(defaultDate || today);
  const [busy, setBusy] = useState(false);

  // Create state
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newItems, setNewItems] = useState([blankItem()]);

  function blankItem() {
    return { category_slug: 'nutrition.supplement', entry_type: '', value: '', value_unit: '' };
  }

  const loadTemplates = async () => {
    try {
      const res = await apiClient.get('/api/templates');
      setTemplates(res.data || []);
    } catch (e) {
      setError('Could not load your templates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    apiClient
      .get('/api/health/categories')
      .then((res) => setCategories(res.data || []))
      .catch(() => {});
  }, []);

  // --- Apply -----------------------------------------------------------------
  // A {ref:"regimen"} item is a live placeholder: at apply time we expand it into
  // one editable row per active supplement in the current stack (SS2), so the
  // template always reflects today's stack, not a frozen copy.
  const openApply = async (t) => {
    setNote('');
    setApplyingId(t.id);
    setApplyDate(defaultDate || today);
    const items = t.items || [];
    let stack = [];
    if (items.some((it) => it.ref === 'regimen')) {
      try {
        const res = await apiClient.get('/api/regimen', {
          params: { kind: 'supplement', active_only: true },
        });
        stack = res.data || [];
      } catch {
        stack = [];
      }
    }
    const expanded = [];
    for (const it of items) {
      if (it.ref === 'regimen') {
        for (const s of stack) {
          expanded.push({
            category_slug: 'nutrition.supplement',
            entry_type: s.name,
            value: s.dose ?? '',
            value_unit: s.dose_unit ?? '',
            data: { supplement_name: s.name, regimen_item_id: s.id, catalog_slug: s.catalog_slug },
            include: true,
          });
        }
      } else {
        expanded.push({ ...it, include: true, value: it.value ?? '' });
      }
    }
    setRows(expanded);
  };

  const toggleRow = (i) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, include: !r.include } : r)));

  const editRowValue = (i, v) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, value: v } : r)));

  const submitApply = async (t) => {
    const chosen = rows.filter((r) => r.include);
    if (chosen.length === 0) {
      setNote('Nothing checked — pick at least one item.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = chosen.map((r) => ({
        entry_date: applyDate,
        category_slug: r.category_slug,
        entry_type: r.entry_type,
        entry_time: r.entry_time ?? null,
        duration_minutes: r.duration_minutes ?? null,
        value: r.value === '' || r.value == null ? null : Number(r.value),
        value_unit: r.value_unit ?? null,
        secondary_value: r.secondary_value ?? null,
        secondary_unit: r.secondary_unit ?? null,
        calories: r.calories ?? null,
        rating: r.rating ?? null,
        notes: r.notes ?? null,
        data: r.data ?? null,
        source: 'template',
      }));
      await apiClient.post('/api/health/entries', payload);
      apiClient.post(`/api/templates/${t.id}/used`).catch(() => {});
      setApplyingId(null);
      setNote(`Logged ${chosen.length} ${chosen.length === 1 ? 'item' : 'items'} from “${t.name}”.`);
      if (onEntrySaved) onEntrySaved();
      loadTemplates();
    } catch (e) {
      setError('Could not log those entries. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // --- Create ----------------------------------------------------------------
  const setNewItem = (i, patch) =>
    setNewItems((its) => its.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const addNewItem = () => setNewItems((its) => [...its, blankItem()]);
  const addRegimenRef = () =>
    setNewItems((its) => [...its, { ref: 'regimen', kind: 'supplement' }]);
  const removeNewItem = (i) => setNewItems((its) => its.filter((_, idx) => idx !== i));

  const resetCreate = () => {
    setCreating(false);
    setNewName('');
    setNewItems([blankItem()]);
  };

  const submitCreate = async () => {
    const items = newItems
      .map((it) => {
        if (it.ref === 'regimen') return { ref: 'regimen', kind: it.kind || 'supplement' };
        if (it.category_slug && it.entry_type.trim()) {
          return {
            category_slug: it.category_slug,
            entry_type: it.entry_type.trim(),
            value: it.value === '' ? null : Number(it.value),
            value_unit: it.value_unit.trim() || null,
          };
        }
        return null;
      })
      .filter(Boolean);
    if (!newName.trim() || items.length === 0) {
      setNote('A template needs a name and at least one filled-in item.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/api/templates', { name: newName.trim(), items });
      resetCreate();
      setNote('Template saved.');
      loadTemplates();
    } catch (e) {
      setError('Could not save the template.');
    } finally {
      setBusy(false);
    }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete the “${t.name}” template? Your logged entries stay.`)) return;
    try {
      await apiClient.delete(`/api/templates/${t.id}`);
      loadTemplates();
    } catch (e) {
      setError('Could not delete that template.');
    }
  };

  // --- Render ----------------------------------------------------------------
  return (
    <div className="rounded-xl border border-surface-border bg-surface-card dark:border-surface-dark-border dark:bg-surface-dark-card p-5 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick log</h2>
          <p className="text-sm text-stone-600 dark:text-stone-300">
            Save a stack or routine once, log it in one tap — tweak before it saves.
          </p>
        </div>
        {!creating && (
          <Button size="sm" variant="outline" onClick={() => { setCreating(true); setNote(''); }}>
            + New template
          </Button>
        )}
      </div>

      {error && (
        <p className="text-sm text-off-strong dark:text-off">{error}</p>
      )}
      {note && (
        <p className="text-sm text-good-strong dark:text-good">{note}</p>
      )}

      {/* Create panel */}
      {creating && (
        <div className="rounded-lg border border-surface-border dark:border-surface-dark-border p-4 space-y-3">
          <div className="space-y-1.5">
            <label className={labelCls}>Template name</label>
            <input
              className={inputCls}
              placeholder="Morning stack"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <span className={labelCls}>Items</span>
            {newItems.map((it, i) => (
              it.ref === 'regimen' ? (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 rounded-lg border border-primary-300 bg-primary-50 dark:border-primary-500/40 dark:bg-primary-500/10 px-3 py-2"
                >
                  <span className="text-sm font-medium text-primary-800 dark:text-primary-200">
                    🧬 My active supplement stack
                    <span className="font-normal text-primary-700/80 dark:text-primary-300/80">
                      {' '}· expands to today’s stack when logged
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeNewItem(i)}
                    className="text-sm text-primary-700 hover:text-off-strong dark:text-primary-300 px-1"
                    aria-label="Remove supplement stack reference"
                  >
                    ✕
                  </button>
                </div>
              ) : (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select
                  className={`${inputCls} w-auto min-w-[9rem] flex-1`}
                  value={it.category_slug}
                  onChange={(e) => setNewItem(i, { category_slug: e.target.value })}
                >
                  {categories.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.name}</option>
                  ))}
                </select>
                <input
                  className={`${inputCls} w-auto flex-1 min-w-[8rem]`}
                  placeholder="what (e.g. vitamin d3)"
                  value={it.entry_type}
                  onChange={(e) => setNewItem(i, { entry_type: e.target.value })}
                />
                <input
                  className={`${inputCls} w-20`}
                  placeholder="amt"
                  inputMode="decimal"
                  value={it.value}
                  onChange={(e) => setNewItem(i, { value: e.target.value })}
                />
                <input
                  className={`${inputCls} w-20`}
                  placeholder="unit"
                  value={it.value_unit}
                  onChange={(e) => setNewItem(i, { value_unit: e.target.value })}
                />
                {newItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeNewItem(i)}
                    className="text-sm text-gray-500 hover:text-off-strong dark:text-gray-400 px-1"
                    aria-label="Remove item"
                  >
                    ✕
                  </button>
                )}
              </div>
              )
            ))}
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={addNewItem}
                className="text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline"
              >
                + Add item
              </button>
              {!newItems.some((it) => it.ref === 'regimen') && (
                <button
                  type="button"
                  onClick={addRegimenRef}
                  className="text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline"
                >
                  + My supplement stack
                </button>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={submitCreate} disabled={busy}>Save template</Button>
            <Button size="sm" variant="ghost" onClick={resetCreate} disabled={busy}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Template list */}
      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : templates.length === 0 && !creating ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No templates yet. Make one for a stack or routine you log often.
        </p>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div
              key={t.id}
              className="rounded-lg border border-surface-border dark:border-surface-dark-border"
            >
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {t.icon && <span aria-hidden>{t.icon}</span>}
                    <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {t.name}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      · {t.items.length} {t.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {applyingId === t.id ? (
                    <Button size="sm" variant="ghost" onClick={() => setApplyingId(null)}>Close</Button>
                  ) : (
                    <Button size="sm" onClick={() => openApply(t)}>Log</Button>
                  )}
                  <button
                    type="button"
                    onClick={() => deleteTemplate(t)}
                    className="text-sm text-gray-400 hover:text-off-strong px-1"
                    aria-label={`Delete ${t.name}`}
                  >
                    🗑
                  </button>
                </div>
              </div>

              {/* Adjustable apply panel */}
              {applyingId === t.id && (
                <div className="border-t border-surface-border dark:border-surface-dark-border p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <label className={labelCls}>Date</label>
                    <input
                      type="date"
                      className={`${inputCls} w-auto`}
                      value={applyDate}
                      onChange={(e) => setApplyDate(e.target.value)}
                    />
                  </div>
                  <ul className="space-y-2">
                    {rows.map((r, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={r.include}
                          onChange={() => toggleRow(i)}
                          className="h-4 w-4 accent-primary-600"
                          aria-label={`Include ${itemLabel(r)}`}
                        />
                        <span
                          className={`text-sm capitalize flex-1 ${
                            r.include
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-400 line-through'
                          }`}
                        >
                          {itemLabel(r)}
                        </span>
                        <input
                          className={`${inputCls} w-20`}
                          inputMode="decimal"
                          value={r.value}
                          disabled={!r.include}
                          onChange={(e) => editRowValue(i, e.target.value)}
                          aria-label={`Amount for ${itemLabel(r)}`}
                        />
                        <span className="text-sm text-gray-500 dark:text-gray-400 w-12">
                          {r.value_unit || ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" onClick={() => submitApply(t)} disabled={busy}>
                    {busy ? 'Logging…' : `Log ${rows.filter((r) => r.include).length} items`}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplatePicker;
